import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { api, ApiError } from '../lib/api.js';
import { AskQuestionModal, type AskQuestionInput } from '../components/qa/AskQuestionModal.js';

type QAQuestion = {
  id: string;
  title: string;
  status: 'open' | 'answered' | 'closed';
  tags: string[];
  asker: { name: string; email: string };
  upvotes: number;
  replyCount: number;
  createdAt: string;
};

type QAResponse = { questions: QAQuestion[]; total: number };

export function QA() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const tagFilter = searchParams.get('tag');
  const [modalOpen, setModalOpen] = useState(false);
  const [postError, setPostError] = useState<string | null>(null);

  const qaQuery = useQuery<QAResponse>({
    queryKey: ['qa', tagFilter],
    queryFn: () =>
      api.get<QAResponse>(tagFilter ? `/api/qa?tag=${encodeURIComponent(tagFilter)}` : '/api/qa'),
  });

  const askMutation = useMutation({
    mutationFn: (input: AskQuestionInput) =>
      api.post<{ id: string }>('/api/qa', input),
    onSuccess: (data) => {
      setModalOpen(false);
      setPostError(null);
      qc.invalidateQueries({ queryKey: ['qa'] });
      navigate(`/qa/${data.id}`);
    },
    onError: (err) => {
      setPostError(err instanceof ApiError ? err.message : 'Could not post question.');
    },
  });

  // Track in-flight upvote IDs locally so a single click on row A doesn't
  // disable every other row's upvote button (the React Query mutation's
  // isPending is shared across all calls).
  const [pendingUpvotes, setPendingUpvotes] = useState<Set<string>>(new Set());
  const upvoteMutation = useMutation({
    mutationFn: (id: string) =>
      api.post<{ ok: boolean; upvotes: number; upvoted: boolean }>(`/api/qa/${id}/upvote`),
    onMutate: (id) => {
      setPendingUpvotes((prev) => {
        const next = new Set(prev);
        next.add(id);
        return next;
      });
    },
    onSuccess: (data, id) => {
      qc.setQueryData<QAResponse>(['qa', tagFilter], (prev) => prev
        ? { ...prev, questions: prev.questions.map((q) => q.id === id ? { ...q, upvotes: data.upvotes } : q) }
        : prev);
      qc.invalidateQueries({ queryKey: ['qa-detail', id] });
    },
    onSettled: (_data, _err, id) => {
      setPendingUpvotes((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    },
  });

  const setTag = (tag: string | null) => {
    setSearchParams((sp) => {
      if (tag) sp.set('tag', tag);
      else sp.delete('tag');
      return sp;
    });
  };

  return (
    <div className="view" style={{ maxWidth: 1240, margin: '0 auto', padding: '36px 32px 96px' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-end',
          flexWrap: 'wrap',
          gap: 18,
          marginBottom: 22,
        }}
      >
        <div>
          <div className="eyebrow">▌ Community · ask anything</div>
          <h1 style={{ fontSize: 32, fontWeight: 700, letterSpacing: '-0.03em', margin: '6px 0 0' }}>
            Q&A — what's everyone stuck on.
          </h1>
          <p style={{ color: 'var(--color-text-3)', marginTop: 6, fontSize: 13 }}>
            Async, by Vimers, for Vimers.
          </p>
        </div>
        <button className="btn btn-primary" onClick={() => setModalOpen(true)}>
          + Ask a question
        </button>
      </div>

      {tagFilter && (
        <div style={{ marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8, fontSize: 12 }}>
          <span style={{ color: 'var(--color-text-3)' }}>Filter:</span>
          <span
            className="chip chip-accent"
            style={{ fontSize: 11, display: 'inline-flex', alignItems: 'center', gap: 6 }}
          >
            {tagFilter}
            <button
              type="button"
              onClick={() => setTag(null)}
              style={{ background: 'transparent', border: 0, color: 'inherit', cursor: 'pointer', padding: 0, fontSize: 12 }}
              aria-label="Clear filter"
              title="Clear filter"
            >
              ✕
            </button>
          </span>
        </div>
      )}

      {postError && (
        <div
          style={{
            marginBottom: 14,
            padding: '10px 12px',
            borderRadius: 8,
            background: 'rgba(255,110,110,0.08)',
            border: '1px solid rgba(255,110,110,0.28)',
            color: 'var(--color-danger)',
            fontSize: 13,
          }}
        >
          {postError}
        </div>
      )}

      {qaQuery.isLoading && (
        <div className="card" style={{ padding: 32, color: 'var(--color-text-3)' }}>Loading…</div>
      )}

      {!qaQuery.isLoading && (qaQuery.data?.questions.length ?? 0) === 0 && (
        <div className="card" style={{ padding: '36px 28px', textAlign: 'center' }}>
          <div className="eyebrow" style={{ marginBottom: 8 }}>▌ Nothing yet</div>
          <h3 style={{ fontSize: 18, fontWeight: 600, margin: '0 0 8px' }}>
            {tagFilter ? `No questions tagged "${tagFilter}".` : 'Be the first to ask a question.'}
          </h3>
          <p style={{ color: 'var(--color-text-3)', fontSize: 13, margin: '0 0 18px' }}>
            {tagFilter
              ? <>Try clearing the filter, or open a new question with this tag.</>
              : <>Async, judgment-free, useful for everyone after you. Go.</>}
          </p>
          <button className="btn btn-primary" onClick={() => setModalOpen(true)}>
            + Ask a question
          </button>
        </div>
      )}

      {!qaQuery.isLoading && (qaQuery.data?.questions.length ?? 0) > 0 && (
        <div className="card" style={{ padding: 0 }}>
          {qaQuery.data!.questions.map((q) => (
            <div
              key={q.id}
              style={{
                display: 'grid',
                gridTemplateColumns: '60px 1fr 90px',
                gap: 16,
                alignItems: 'center',
                padding: '18px 20px',
                borderBottom: '1px solid var(--color-border)',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 2,
                  padding: '8px 6px',
                  borderRadius: 8,
                  background: 'var(--color-bg-elevated)',
                  border: '1px solid var(--color-border)',
                }}
              >
                <button
                  onClick={() => upvoteMutation.mutate(q.id)}
                  disabled={pendingUpvotes.has(q.id)}
                  style={{
                    background: 'transparent',
                    border: 0,
                    color: 'var(--color-text-3)',
                    fontSize: 12,
                    cursor: pendingUpvotes.has(q.id) ? 'wait' : 'pointer',
                    padding: 2,
                  }}
                  aria-label={`Upvote ${q.title}`}
                  title="Upvote"
                >
                  ▲
                </button>
                <span style={{ fontWeight: 700, fontSize: 14 }}>{q.upvotes}</span>
                <span style={{ color: 'var(--color-text-3)', fontSize: 11 }}>votes</span>
              </div>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 5, flexWrap: 'wrap' }}>
                  <span
                    className={q.status === 'answered' ? 'chip chip-accent' : 'chip chip-warn'}
                    style={{ fontSize: 10 }}
                  >
                    {q.status === 'answered' ? '✓ Answered' : q.status === 'open' ? '🟠 Open' : '⚫ Closed'}
                  </span>
                  {q.tags.slice(0, 5).map((t) => (
                    <button
                      key={t}
                      type="button"
                      onClick={() => setTag(t)}
                      className="chip"
                      style={{
                        fontSize: 10,
                        cursor: 'pointer',
                        background: tagFilter === t ? 'var(--color-accent-soft)' : undefined,
                        color: tagFilter === t ? 'var(--color-accent)' : undefined,
                      }}
                      title={`Filter by ${t}`}
                    >
                      {t}
                    </button>
                  ))}
                </div>
                <Link
                  to={`/qa/${q.id}`}
                  style={{
                    fontSize: 15,
                    fontWeight: 600,
                    letterSpacing: '-0.01em',
                    lineHeight: 1.4,
                    marginBottom: 4,
                    color: 'var(--color-text)',
                    textDecoration: 'none',
                    display: 'block',
                  }}
                >
                  {q.title}
                </Link>
                <div style={{ fontSize: 11.5, color: 'var(--color-text-3)' }}>
                  {q.asker.name || q.asker.email || 'someone'} · {q.replyCount} {q.replyCount === 1 ? 'reply' : 'replies'}
                </div>
              </div>
              <div style={{ textAlign: 'right', fontSize: 11, color: 'var(--color-text-3)' }}>
                💬 {q.replyCount}
              </div>
            </div>
          ))}
        </div>
      )}

      {modalOpen && (
        <AskQuestionModal
          busy={askMutation.isPending}
          onSubmit={(input) => askMutation.mutate(input)}
          onClose={() => { setModalOpen(false); setPostError(null); }}
        />
      )}
    </div>
  );
}
